import type { Security } from '../../proto/generated/security'
import type { OptionSeriesDef, SecuritiesMeta, SecuritiesSnapshot } from './types'
import { optionSeriesId, securityKey } from './types'

const DB_NAME = 't2t-securities'
const DB_VERSION = 1
const STORE_SECURITIES = 'securities'
const STORE_OPTION_SERIES = 'optionSeries'
const STORE_META = 'meta'
const META_KEY = 'securities'

function openDb(): Promise<IDBDatabase | null> {
    return new Promise((resolve) => {
        if (typeof indexedDB === 'undefined') {
            resolve(null)
            return
        }
        let request: IDBOpenDBRequest
        try {
            request = indexedDB.open(DB_NAME, DB_VERSION)
        } catch {
            resolve(null)
            return
        }
        request.onupgradeneeded = () => {
            const db = request.result
            for (const store of [STORE_SECURITIES, STORE_OPTION_SERIES, STORE_META]) {
                if (!db.objectStoreNames.contains(store)) db.createObjectStore(store)
            }
        }
        request.onsuccess = () => resolve(request.result)
        request.onerror = () => resolve(null)
        request.onblocked = () => resolve(null)
    })
}

function awaitRequest<T>(request: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result)
        request.onerror = () => reject(request.error)
    })
}

function awaitTransaction(tx: IDBTransaction): Promise<void> {
    return new Promise((resolve, reject) => {
        tx.oncomplete = () => resolve()
        tx.onerror = () => reject(tx.error)
        tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'))
    })
}

export async function readSecuritiesMeta(): Promise<SecuritiesMeta | null> {
    const db = await openDb()
    if (!db) return null
    try {
        const tx = db.transaction(STORE_META, 'readonly')
        const meta = await awaitRequest(tx.objectStore(STORE_META).get(META_KEY))
        return (meta as SecuritiesMeta | undefined) ?? null
    } catch {
        return null
    } finally {
        db.close()
    }
}

/** Returns null when the store is empty or unavailable, so callers fall back to the network. */
export async function readSecuritiesSnapshot(): Promise<SecuritiesSnapshot | null> {
    const db = await openDb()
    if (!db) return null
    try {
        const tx = db.transaction([STORE_SECURITIES, STORE_OPTION_SERIES], 'readonly')
        const [securities, optionSeries] = await Promise.all([
            awaitRequest(tx.objectStore(STORE_SECURITIES).getAll()) as Promise<Security[]>,
            awaitRequest(tx.objectStore(STORE_OPTION_SERIES).getAll()) as Promise<OptionSeriesDef[]>,
        ])
        if (!securities?.length) return null
        return { securities, optionSeries: optionSeries ?? [] }
    } catch {
        return null
    } finally {
        db.close()
    }
}

/** Replaces the whole store atomically: securities keyed by security key, option series by id. */
export async function writeSecuritiesSnapshot(snapshot: SecuritiesSnapshot, meta: SecuritiesMeta): Promise<boolean> {
    const db = await openDb()
    if (!db) return false
    try {
        const tx = db.transaction([STORE_SECURITIES, STORE_OPTION_SERIES, STORE_META], 'readwrite')
        const securities = tx.objectStore(STORE_SECURITIES)
        const optionSeries = tx.objectStore(STORE_OPTION_SERIES)
        securities.clear()
        optionSeries.clear()
        for (const sec of snapshot.securities) securities.put(sec, securityKey(sec.exchange, sec.code))
        for (const def of snapshot.optionSeries) optionSeries.put(def, optionSeriesId(def.exchange, def.securityCodePrefix))
        tx.objectStore(STORE_META).put(meta, META_KEY)
        await awaitTransaction(tx)
        return true
    } catch {
        return false
    } finally {
        db.close()
    }
}

export async function clearSecuritiesDb(): Promise<void> {
    const db = await openDb()
    if (!db) return
    try {
        const tx = db.transaction([STORE_SECURITIES, STORE_OPTION_SERIES, STORE_META], 'readwrite')
        tx.objectStore(STORE_SECURITIES).clear()
        tx.objectStore(STORE_OPTION_SERIES).clear()
        tx.objectStore(STORE_META).clear()
        await awaitTransaction(tx)
    } catch {
        /* ignore */
    } finally {
        db.close()
    }
}
